import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, Download, Undo2, ShieldCheck, Clock, User, Briefcase, ShieldAlert } from "lucide-react";

const T = {
  pt: {
    overline: "Administração",
    title: "Painel Admin",
    tab_pending: "Solicitações Pendentes",
    tab_users: "Usuários & Licenças",
    empty_pending: "Nenhuma solicitação pendente no momento.",
    approve: "Aprovar & Liberar PDF",
    unapprove: "Desfazer aprovação",
    download: "Baixar PDF",
    approved: "PDF Aprovado",
    pending: "Aguardando revisão",
    client: "Cliente",
    form: "Formulário",
    created: "Criado",
    actions: "Ações",
    activate_30: "+30 dias",
    activate_90: "+90 dias",
    activate_365: "+365 dias",
    make_particular: "Tornar Particular",
    license_active: "Licença Ativa",
    license_pending: "Pendente",
    license_expired: "Expirada",
    email: "Email",
    type: "Tipo",
    expires: "Expira",
    no_access: "Você não tem permissão para acessar esta página.",
  },
  es: {
    overline: "Administración",
    title: "Panel Admin",
    tab_pending: "Solicitudes Pendientes",
    tab_users: "Usuarios & Licencias",
    empty_pending: "No hay solicitudes pendientes en este momento.",
    approve: "Aprobar & Liberar PDF",
    unapprove: "Deshacer aprobación",
    download: "Descargar PDF",
    approved: "PDF Aprobado",
    pending: "Esperando revisión",
    client: "Cliente",
    form: "Formulario",
    created: "Creado",
    actions: "Acciones",
    activate_30: "+30 días",
    activate_90: "+90 días",
    activate_365: "+365 días",
    make_particular: "Cambiar a Particular",
    license_active: "Licencia Activa",
    license_pending: "Pendiente",
    license_expired: "Expirada",
    email: "Email",
    type: "Tipo",
    expires: "Expira",
    no_access: "No tiene permiso para acceder a esta página.",
  },
  en: {
    overline: "Administration",
    title: "Admin Panel",
    tab_pending: "Pending Requests",
    tab_users: "Users & Licenses",
    empty_pending: "No pending requests at the moment.",
    approve: "Approve & Release PDF",
    unapprove: "Undo approval",
    download: "Download PDF",
    approved: "PDF Approved",
    pending: "Awaiting review",
    client: "Client",
    form: "Form",
    created: "Created",
    actions: "Actions",
    activate_30: "+30 days",
    activate_90: "+90 days",
    activate_365: "+365 days",
    make_particular: "Set as Individual",
    license_active: "Active License",
    license_pending: "Pending",
    license_expired: "Expired",
    email: "Email",
    type: "Type",
    expires: "Expires",
    no_access: "You don't have permission to access this page.",
  },
};

export default function AdminPage() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const tr = T[lang] || T.pt;
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const isAdmin = user?.role === "admin" || user?.license?.license_type === "admin";

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/pending-forms");
      setPending(res.data.forms || []);
    } catch (e) {
      toast.error("Failed to load pending forms");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "pending") loadPending();
    else loadUsers();
  }, [tab, isAdmin, loadPending, loadUsers]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center" data-testid="admin-no-access">
        <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
        <p className="mt-4 text-sm text-muted-foreground">{tr.no_access}</p>
      </div>
    );
  }

  const approve = async (formId) => {
    try {
      await api.post(`/admin/forms/${formId}/approve`, { admin_notes: "" });
      toast.success(lang === "pt" ? "PDF liberado" : lang === "es" ? "PDF liberado" : "PDF released");
      loadPending();
    } catch (e) {
      toast.error("Failed to approve");
    }
  };

  const unapprove = async (formId) => {
    try {
      await api.post(`/admin/forms/${formId}/unapprove`);
      loadPending();
    } catch (e) {
      toast.error("Failed to unapprove");
    }
  };

  const downloadAdminPdf = async (formId, code) => {
    try {
      const res = await api.get(`/admin/forms/${formId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `USCIS_${code}_${formId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download");
    }
  };

  const updateLicense = async (userId, licenseType, days = 30) => {
    try {
      await api.post(`/admin/users/${userId}/license`, { license_type: licenseType, days });
      toast.success(lang === "pt" ? "Licença atualizada" : lang === "es" ? "Licencia actualizada" : "License updated");
      loadUsers();
    } catch (e) {
      toast.error("Failed to update license");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-12" data-testid="admin-page">
      {/* Header */}
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))]">
          {tr.overline}
        </div>
        <h1 className="font-display mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
          {tr.title}
        </h1>
        <div className="mt-4 h-px w-24 bg-[hsl(var(--gold))]/60" />
      </div>

      {/* Tabs */}
      <div className="mb-8 flex gap-2 border-b border-border">
        <TabButton active={tab === "pending"} onClick={() => setTab("pending")} testid="admin-tab-pending">
          {tr.tab_pending}
          <span className="ml-2 rounded-full bg-[hsl(var(--gold))]/15 px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--gold))]">
            {pending.filter((f) => !f.admin_approved).length}
          </span>
        </TabButton>
        <TabButton active={tab === "users"} onClick={() => setTab("users")} testid="admin-tab-users">
          {tr.tab_users}
        </TabButton>
      </div>

      {/* Pending */}
      {tab === "pending" && (
        <PendingList
          items={pending}
          tr={tr}
          lang={lang}
          onApprove={approve}
          onUnapprove={unapprove}
          onDownload={downloadAdminPdf}
          loading={loading}
        />
      )}

      {/* Users */}
      {tab === "users" && (
        <UsersList
          items={users}
          tr={tr}
          lang={lang}
          onLicense={updateLicense}
          loading={loading}
        />
      )}
    </div>
  );
}

function TabButton({ children, active, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`relative flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
        active ? "text-[hsl(var(--gold))]" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
      {active && (
        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[hsl(var(--gold))]" />
      )}
    </button>
  );
}

function PendingList({ items, tr, lang, onApprove, onUnapprove, onDownload, loading }) {
  if (loading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (!items.length) {
    return (
      <div className="hairline rounded-lg bg-card p-16 text-center text-sm text-muted-foreground">
        {tr.empty_pending}
      </div>
    );
  }
  return (
    <div className="hairline overflow-hidden rounded-lg bg-card">
      <div className="grid grid-cols-12 border-b border-border bg-secondary/50 px-6 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        <div className="col-span-3">{tr.client}</div>
        <div className="col-span-2">{tr.form}</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2">{tr.created}</div>
        <div className="col-span-3 text-right">{tr.actions}</div>
      </div>
      <ul>
        {items.map((f) => (
          <li
            key={f.id}
            data-testid={`admin-pending-row-${f.id}`}
            className="grid grid-cols-12 items-center border-b border-border px-6 py-4 last:border-0"
          >
            <div className="col-span-3">
              <div className="font-semibold text-sm">{f.client_name || f.client_email}</div>
              <div className="font-mono text-[10px] text-muted-foreground">{f.client_email}</div>
            </div>
            <div className="col-span-2">
              <div className="font-display text-lg font-bold text-[hsl(var(--gold))]">{f.form_code}</div>
              <div className="font-mono text-[9px] uppercase text-muted-foreground">{f.form_type}</div>
            </div>
            <div className="col-span-2">
              {f.admin_approved ? (
                <Badge className="rounded-md bg-[hsl(var(--sage))] font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--sage-foreground))]">
                  <CheckCircle2 className="mr-1 h-3 w-3" />
                  {tr.approved}
                </Badge>
              ) : (
                <Badge variant="secondary" className="rounded-md font-mono text-[10px] uppercase tracking-widest">
                  <Clock className="mr-1 h-3 w-3" />
                  {tr.pending}
                </Badge>
              )}
            </div>
            <div className="col-span-2 font-mono text-xs text-muted-foreground">
              {new Date(f.created_at).toLocaleString(lang === "es" ? "es-ES" : lang === "en" ? "en-US" : "pt-BR")}
            </div>
            <div className="col-span-3 flex justify-end gap-2">
              {!f.admin_approved ? (
                <Button
                  size="sm"
                  data-testid={`admin-approve-${f.id}`}
                  onClick={() => onApprove(f.id)}
                  className="rounded-md bg-[hsl(var(--gold))] font-semibold text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-soft))]"
                >
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                  {tr.approve}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`admin-unapprove-${f.id}`}
                  onClick={() => onUnapprove(f.id)}
                  className="rounded-md"
                >
                  <Undo2 className="mr-1 h-3.5 w-3.5" />
                  {tr.unapprove}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                data-testid={`admin-download-${f.id}`}
                onClick={() => onDownload(f.id, f.form_code)}
                className="rounded-md"
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UsersList({ items, tr, lang, onLicense, loading }) {
  if (loading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  return (
    <div className="hairline overflow-hidden rounded-lg bg-card">
      <div className="grid grid-cols-12 border-b border-border bg-secondary/50 px-6 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        <div className="col-span-3">{tr.email}</div>
        <div className="col-span-2">{tr.type}</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2">{tr.expires}</div>
        <div className="col-span-3 text-right">{tr.actions}</div>
      </div>
      <ul>
        {items.map((u) => (
          <li
            key={u.id}
            data-testid={`admin-user-row-${u.id}`}
            className="grid grid-cols-12 items-center border-b border-border px-6 py-4 last:border-0"
          >
            <div className="col-span-3">
              <div className="font-semibold text-sm">{u.name || u.email}</div>
              <div className="font-mono text-[10px] text-muted-foreground">{u.email}</div>
            </div>
            <div className="col-span-2">
              {u.license.license_type === "admin" ? (
                <Badge className="rounded-md bg-destructive font-mono text-[10px] uppercase tracking-widest">
                  <ShieldCheck className="mr-1 h-3 w-3" />
                  Admin
                </Badge>
              ) : u.license.license_type === "comercial" ? (
                <Badge className="rounded-md bg-[hsl(var(--gold))] font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--primary-foreground))]">
                  <Briefcase className="mr-1 h-3 w-3" />
                  Comercial
                </Badge>
              ) : (
                <Badge variant="secondary" className="rounded-md font-mono text-[10px] uppercase tracking-widest">
                  <User className="mr-1 h-3 w-3" />
                  Particular
                </Badge>
              )}
            </div>
            <div className="col-span-2">
              {u.license.status === "active" ? (
                <span className="text-xs font-semibold text-[hsl(var(--sage))]">{tr.license_active}</span>
              ) : u.license.status === "pending" ? (
                <span className="text-xs text-muted-foreground">{tr.license_pending}</span>
              ) : (
                <span className="text-xs font-semibold text-destructive">{tr.license_expired}</span>
              )}
            </div>
            <div className="col-span-2 font-mono text-xs text-muted-foreground">
              {u.license.expires_at
                ? new Date(u.license.expires_at).toLocaleDateString(lang === "es" ? "es-ES" : lang === "en" ? "en-US" : "pt-BR")
                : "—"}
            </div>
            <div className="col-span-3 flex flex-wrap justify-end gap-1">
              {u.license.license_type !== "admin" && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`admin-license-30-${u.id}`}
                    onClick={() => onLicense(u.id, "comercial", 30)}
                    className="rounded-md text-xs"
                  >
                    {tr.activate_30}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`admin-license-90-${u.id}`}
                    onClick={() => onLicense(u.id, "comercial", 90)}
                    className="rounded-md text-xs"
                  >
                    {tr.activate_90}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`admin-license-365-${u.id}`}
                    onClick={() => onLicense(u.id, "comercial", 365)}
                    className="rounded-md text-xs"
                  >
                    {tr.activate_365}
                  </Button>
                  {u.license.license_type === "comercial" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`admin-license-particular-${u.id}`}
                      onClick={() => onLicense(u.id, "particular", 0)}
                      className="rounded-md text-xs text-muted-foreground"
                    >
                      {tr.make_particular}
                    </Button>
                  )}
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
